import { useCallback, useRef, useState } from 'react';
import logger from '@utils/logger';
import {
  GetSubcategoryRecommendationInput,
  GetSubcategoryRecommendationQuery,
  GetSubcategoryRecommendationQueryVariables,
  GetSubcategoryRecommendationDocument,
  SubcategoryRecommendation,
} from '@graphql/generated/graphql';
import { fetcher } from '@graphql/utils/fetcher';

interface UseGetSubcategoryRecommendationInput {
  storeId: string;
  macroplanogramId: string;
  itemIds: string[];
  itemType?: string;
  onSuccess?: (recommendations: SubcategoryRecommendation[]) => void;
  onError?: (error: string) => void;
}

interface UseGetSubcategoryRecommendationResult {
  fetchRecommendations: () => void;
  isLoading: boolean;
  error: string | null;
  data: SubcategoryRecommendation[] | null;
  reset: () => void;
}

/**
 * Imperative hook for fetching subcategory recommendations on demand.
 * Does NOT auto-fire on mount or re-render — only when fetchRecommendations() is called.
 */
const useGetSubcategoryRecommendation = (
  input: UseGetSubcategoryRecommendationInput,
): UseGetSubcategoryRecommendationResult => {
  const { storeId, macroplanogramId, itemIds, itemType = 'SCANCODE', onSuccess, onError } = input;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SubcategoryRecommendation[] | null>(null);

  // Use refs to always read the latest callbacks and itemIds at call time
  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const fetchRecommendations = useCallback(async () => {
    const currentItemIds = itemIdsRef.current;
    if (currentItemIds.length === 0) return;

    setIsLoading(true);
    setError(null);
    setData(null);

    const queryInput: GetSubcategoryRecommendationInput = {
      storeId,
      macroplanogramId,
      itemIds: currentItemIds,
      itemType,
    };

    try {
      const variables: GetSubcategoryRecommendationQueryVariables = { input: queryInput };
      const result = await fetcher<GetSubcategoryRecommendationQuery, GetSubcategoryRecommendationQueryVariables>(
        GetSubcategoryRecommendationDocument,
        variables,
      )();

      const response = result.getSubcategoryRecommendation;

      if (response.error) {
        const errorMsg = response.error.errorMessage;
        setError(errorMsg);
        onErrorRef.current?.(errorMsg);
      } else {
        const recommendations = (response.recommendations as SubcategoryRecommendation[]) ?? [];
        setData(recommendations);
        onSuccessRef.current?.(recommendations);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to get AI suggestions. Please assign subcategories manually or try again.';
      void logger.error(`Error occurred while fetching subcategory recommendations: ${message}`);
      setError(message);
      onErrorRef.current?.(message);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, macroplanogramId, itemType]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return {
    fetchRecommendations,
    isLoading,
    error,
    data,
    reset,
  };
};

export default useGetSubcategoryRecommendation;
